import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  StyleSheet,
  View,
  ScrollView,
  Platform,
  Pressable,
} from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Text from '@/components/Typography/Text';
import ChatInput from '@/components/ChatInput/ChatInput';
import ComposerModeSwitch from '@/components/ChatInput/ComposerModeSwitch';
import NewChatProjectBar from '@/components/ChatInput/NewChatProjectBar';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import useResponsiveness from '@/hooks/useResponsiveness';
import useComposerKeyboardHeight from '@/hooks/useComposerKeyboardHeight';
import useDefaultChatBot from '@/hooks/useDefaultChatBot';
import { useTranslation } from '@/hooks/useTranslation';
import { useChatStore, useAuthStore, useToastStore, useUIStore, useModalStore, useChatInputStore, useModelStore, useCoworkerStore, useAgentStore } from '@/store';
import { WORK_COMPOSER_PLACEHOLDER, WORK_HOME_HEADLINE } from '@/constants/work';
import socketManager from '@/lib/socket';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import { CHAT_INPUT_STYLE, DEFAULT_IMAGE_MODEL_SLUG, DEFAULT_VIDEO_MODEL_SLUG, DEFAULT_MUSIC_MODEL_SLUG, DEFAULT_3D_MODEL_SLUG, DEFAULT_IMAGE_TO_3D_MODEL_SLUG } from '@/constants/config';
import { MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import ChatInputContainer from '@/components/ChatInput/ChatInputContainer';
import WorkModeIntentSuggestions from '@/components/WorkModeIntentSuggestions';
import WorkSuggestionChips from '@/components/Coworker/WorkSuggestionChips';
import ChatStarterSuggestions from '@/components/ChatStarterSuggestions';
import HomeDiscoveryFeed from '@/components/Discovery/HomeDiscoveryFeed';
import WorkDiscoveryFeed from '@/components/Discovery/WorkDiscoveryFeed';
import DiscoveryInvitation from '@/components/Discovery/DiscoveryInvitation';
import DiscoveryKeyboardActions from '@/components/Discovery/DiscoveryKeyboardActions';
import useHomeDiscoveryNavigation from '@/hooks/useHomeDiscoveryNavigation';
import RemoteWorkLaunchPreview from '@/components/Coworker/RemoteWorkLaunchPreview';
import useRemoteLaunchFeedback from '@/hooks/useRemoteLaunchFeedback';
import BannerPlacement from '@/components/Banner/BannerPlacement';
import useRequireAuth, { useAuthPresentation } from '@/hooks/useRequireAuth';
import { useIsColdStartRestoreSettled } from '@/hooks/useRestoreLastRoute';
import { resolveConfiguredOutputModel } from '@/lib/defaultOutputModels';
import { reportWorkLaunchError } from '@/lib/reportWorkLaunchError';
import { hrefForCreatedWork, navigateToCreatedWork } from '@/lib/startWorkFromPrompt';
import { blockIfWorkModeDisabled, useWorkModeDisabled } from '@/lib/workModeAvailability';

function BotHero({ agent, compact = false }) {
  const { colors } = useTheme();
  const [failedSource, setFailedSource] = useState(null);
  const size = compact ? 72 : 96;
  const source = agent.avatarPath;
  return (
    <View accessibilityRole="image" accessible accessibilityLabel={`${agent.name} avatar`}
      style={{ width: size, height: size, borderRadius: size / 4, overflow: 'hidden',
        alignSelf: 'center', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
        backgroundColor: colors.solid_color_tertiary }}>
      {source && source !== failedSource ? (
        <Image source={{ uri: source }} contentFit="cover" accessible={false}
          style={{ width: size, height: size }} onError={() => setFailedSource(source)} />
      ) : <Icon name="robot" size={size / 2} color={colors.text_color_secondary} />}
    </View>
  );
}

// Create styles outside component
// The home page always fills its parent so the mode switch can sit at the
// top while the composer stays vertically centered. isNative still drives
// native-only sizing and background fill.
const createStyles = (colors, isXS, messageWidth, keyboardHeight = 0, bottomInset = 0, isNative = false, pinInputToBottom = false) => {
  // Whenever the software keyboard is open the composer must pin above it at
  // any width — gating this on isXS alone left iPad landscape with the
  // centered composer hidden behind the keyboard.
  const isKeyboardOpen = keyboardHeight > 0;
  return StyleSheet.create({
  container: {
    flex: 1,
    marginVertical: 0,
    marginHorizontal: 0,
    paddingVertical: isXS ? 0 : 20,
    paddingHorizontal: isXS ? 0 : 20,
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: colors.solid_color_primary,
  },
  title: {
    fontSize: isXS ? 30 : 34,
    paddingHorizontal: isXS ? 20 : 0,
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleCompact: {
    fontSize: 24,
  },
  subtitle: {
    fontSize: isXS ? 14 : 16,
    paddingHorizontal: isXS ? 20 : 0,
    textAlign: 'center',
    marginTop: isXS ? 8 : 12,
    maxWidth: '90%',
  },
  titleContainer: {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: isXS ? 0 : 32,
  },
  modeSwitch: {
    alignSelf: 'center',
    width: '100%',
    alignItems: 'center',
    paddingTop: isXS ? 10 : 4,
    paddingBottom: isXS ? 8 : 12,
  },
  middleContainer: {
    width: '100%',
    maxWidth: isXS ? '100%' :
      messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM : MESSAGE_WIDTH_AMOUNTS.NARROW,
    flex: 1,
    alignSelf: 'center',
    justifyContent: 'flex-start',
    ...(isNative ? { backgroundColor: colors.solid_color_primary } : {}),
  },
  centeredContent: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    justifyContent: 'center',
  },
  pinnedColumn: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  pinnedHero: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    justifyContent: 'center',
    paddingTop: !isXS && isKeyboardOpen ? 24 : 0,
  },
  centeredScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 12,
  },
  heroScroll: {
    flex: 1,
    minHeight: 0,
    width: '100%',
  },
  inputContainer: {
    width: '100%',
    zIndex: 2,
    minWidth: 0,
    maxWidth: isXS ? '100%' : 860,
    marginHorizontal: 'auto',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    backgroundColor: pinInputToBottom ? colors.solid_color_primary : 'transparent',
    paddingBottom: pinInputToBottom ? Math.max(keyboardHeight, bottomInset) : 0,
  },
  starterSuggestions: {
    width: '100%',
    paddingHorizontal: 8,
    marginBottom: 6,
  },
  homeActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    // Keep room for both quiet actions when they wrap on a phone.
    minHeight: isXS || isNative ? 44 : 36,
    marginTop: 6,
    maxWidth: '100%',
  },
  homeActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    minHeight: isXS || isNative ? 44 : 36,
    maxWidth: '100%',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
  },
  homeActionLabel: {
    flexShrink: 1,
    textAlign: 'center',
  },
  keyboardTagline: {
    fontSize: 22,
    textAlign: 'center',
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 4,
  },
  });
};

const TAGLINE_COUNT = 10;



const HomePage = React.memo(() => {
  const isFocused = useIsFocused();
  const discoveryRef = useRef(null);
  const homeScrollRef = useRef(null);
  const homeScrollMetrics = useRef({ y: 0, height: 0 });
  const [homeViewportHeight, setHomeViewportHeight] = useState(0);
  const { colors } = useTheme();
  const router = useRouter();
  const { isXS, windowHeight } = useResponsiveness();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const insets = useSafeAreaInsets();
  const requireAuth = useRequireAuth();
  const [workLaunching, setWorkLaunching] = useState(false);
  const { launch: remoteLaunch, begin: beginRemoteLaunch, reset: resetRemoteLaunch } = useRemoteLaunchFeedback();

  // Share the draft with artifact handoffs and suggestion chips. A separate
  // empty local value overwrote prefilled edit requests when home mounted.
  const message = useChatInputStore(state => state.inputStates.new?.text || '');
  const setMessage = useCallback(text => {
    const composer = useChatInputStore.getState();
    composer.setContext('new');
    composer.setText(text);
  }, []);
  const effectiveKeyboardHeight = useComposerKeyboardHeight();
  const isKeyboardOpen = effectiveKeyboardHeight > 0;
  // iPad and native landscape are never XS. Pinning only after the keyboard
  // opened used to remount the composer into a different parent and immediately
  // steal focus (keyboard flashes then dismisses).
  const pinInputToBottom = isXS || Platform.OS === 'ios' || Platform.OS === 'android' || isKeyboardOpen;
  const isShortMobileWeb = Platform.OS === 'web' && isXS
    && windowHeight - effectiveKeyboardHeight < 700;

  const isWorkModeDisabled = useWorkModeDisabled();
  const composerModeIsWork = useCoworkerStore((s) => s.composerMode === 'coworker');
  const isWorkMode = composerModeIsWork && !isWorkModeDisabled;
  const setComposerMode = useCoworkerStore((s) => s.setComposerMode);

  // The flag can flip under a composer already sitting in Work (bootstrap
  // refresh, deep link into /coworker). Send must never launch Work then.
  useEffect(() => {
    if (composerModeIsWork && isWorkModeDisabled) setComposerMode('chat');
  }, [composerModeIsWork, isWorkModeDisabled, setComposerMode]);

  const tagline = useMemo(() => {
    const idx = Math.floor(Math.random() * TAGLINE_COUNT);
    return t(`home.taglines.${idx}`);
  }, [t]);

  const createConversation = useChatStore(state => state.createConversation);

  const isTokenExpired = useAuthStore(state => state.isTokenExpired);
  const token = useAuthStore(state => state.token);
  const messageWidth = useUIStore(state => state.messageWidth);
  const userId = useAuthStore(state => state.user?.id);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const canUpgradeToPlus = useAuthStore(state =>
    !!state.user?.id &&
    !state.user?.flags?.isPlus &&
    !state.user?.flags?.isPlusPremium &&
    !state.user?.flags?.isComped &&
    !state.user?.credits?.business
  );
  // Mirrors the backend inference gate (permissions_validator_service):
  // free accounts flagged as requiring verification must verify before sending.
  const needsEmailVerification = useAuthStore(state =>
    !state.user?.flags?.isPlus &&
    !state.user?.flags?.isGhost &&
    !!state.user?.flags?.isEmailVerificationRequired &&
    !state.user?.flags?.isEmailVerified
  );
  const isAuthenticated = token && !isTokenExpired();
  const presentAuthenticated = useAuthPresentation();

  const params = useLocalSearchParams();
  // Anonymous visitors hitting `/app?temporary-chat=true` (or who somehow
  // toggle the param) get auto-bounced to the regular composer — temp
  // chats only make sense once we have a user to attribute them to.
  const isTemporaryChat = params?.['temporary-chat'] === 'true' && isAuthenticated;
  useDefaultChatBot(presentAuthenticated && !isTemporaryChat);
  const selectedBotId = useChatInputStore(state => state.inputStates.new?.selectedAgentIds?.[0]);
  const selectedBot = useAgentStore(state => state.agents[selectedBotId]);
  const headline = selectedBot ? (isWorkMode ? `Give ${selectedBot.name} a job` : `Chat with ${selectedBot.name}`) : isWorkMode ? WORK_HOME_HEADLINE : (isTemporaryChat ? t('home.temporaryChat') : tagline);

  // Get the current chat input state to check for files
  const setContext = useChatInputStore(state => state.setContext);
  const clearInput = useChatInputStore(state => state.clearInput);
  const pendingAutoSend = useChatInputStore(state => state.pendingAutoSend);
  const clearPendingAutoSend = useChatInputStore(state => state.clearPendingAutoSend);
  const hasComposerFiles = useChatInputStore((state) => {
    const files = state.inputStates?.[state.currentContextId]?.files
      || state.inputStates?.new?.files;
    return Array.isArray(files) && files.length > 0;
  });

  const isNative = Platform.OS !== 'web';
  const isTouchNative = Platform.OS === 'ios' || Platform.OS === 'android';
  const isMobile = Platform.OS === 'ios' || Platform.OS === 'android' || (Platform.OS === 'web' && isXS);
  const useShortChatPlaceholder = isMobile;
  // On native cold start the home composer mounts before route restore
  // navigates to the last chat. Skip autofocus until restore settles so the
  // keyboard doesn't rise and get torn down mid-launch.
  const isColdStartRestoreSettled = useIsColdStartRestoreSettled();
  // Touch-native home opens for browsing, at every width and orientation.
  // Explicit idea and artifact handoffs still request focus through the input store.
  // Preserve the existing desktop autofocus and cold-start restore behavior.
  const canAutofocusComposer = !isTouchNative && !isXS && !Platform.isPad;
  const shouldAutofocusComposer = canAutofocusComposer && (!isNative || isColdStartRestoreSettled);
  const homeComposerKey = (isNative && !isColdStartRestoreSettled) ? 'new-waiting' : 'new';
  // Memoize styles
  const styles = useMemo(() => createStyles(colors, isXS, messageWidth, effectiveKeyboardHeight, insets.bottom, isNative, pinInputToBottom), [colors, isXS, messageWidth, effectiveKeyboardHeight, insets.bottom, isNative, pinInputToBottom]);

  // Anonymous visitors landing on `/app?temporary-chat=true` get the
  // unified auth CTA exactly once per mount — temp chats need a user to
  // attach to. We don't strip the URL param here; the `isTemporaryChat`
  // derivation above already pins it to `false` for anon visitors so the
  // UI doesn't render in temp-chat mode regardless.
  useEffect(() => {
    if (params?.['temporary-chat'] === 'true' && !isAuthenticated) {
      requireAuth(() => {}, {
        source: 'home_temporary_chat',
        title: 'Sign in to use Temporary chat',
        subtitle: 'Temporary chats live on your account so we can clean them up automatically when you’re done.',
        icon: 'eye-slash',
      });
    }
    // Only when authentication state or the query param flips.
  }, [params?.['temporary-chat'], isAuthenticated, requireAuth]);

  // Set up the 'new' context when component mounts AND whenever the screen
  // regains focus (e.g., navigating back from a chat on iOS where screens
  // stay mounted in the back-stack). Without this, the global
  // currentContextId in the store may still point to the chat's ID,
  // causing setText() in the composer to write to the wrong context.
  useFocusEffect(
    useCallback(() => {
      setContext('new');
      // New Chat from a Work thread can keep an editor panel open on /app.
      // That panel is z-index 20 and swallows the home Send button.
      useUIStore.getState().closeContextSidebar?.();
      // Native drawer screens often stay mounted. Work send sets
      // workLaunching and only cleared it on error, so returning to New Chat
      // left Send spinning and disabled (chat and Work).
      setWorkLaunching(false);
    }, [setContext])
  );

  // Memoized handlers - defined before useEffects that depend on them
  const handleSend = useCallback(async (formattedMessage) => {
    if (!formattedMessage) return false;

    // Coworker mode: route the composed text into a new Work thread
    // instead of a chat conversation. Attachments become part of the
    // agent's prompt context (fetch_url-able CDN URLs, server-side).
    if (useCoworkerStore.getState().composerMode === 'coworker') {
      if (blockIfWorkModeDisabled()) return false;
      if (!isPlus) {
        openModal('PlusFeatureModal', { feature: 'work' });
        return false;
      }
      const prompt = String(formattedMessage.text || '').trim();
      if (!prompt) return false;
      const launchOptions = beginRemoteLaunch({ prompt, fileIds: formattedMessage.fileIds });
      if (!launchOptions) return false;
      setWorkLaunching(true);
      try {
        const coworkerState = useCoworkerStore.getState();
        const created = await coworkerState.createTask({
          prompt,
          agentId: formattedMessage.agentIds?.[0],
          fileIds: Array.isArray(formattedMessage.fileIds) ? formattedMessage.fileIds : [],
          ...coworkerState.getComposerLaunchOptions(),
        }, launchOptions);
        // Suppress the old composer's draft restoration after navigation or
        // an account change. Remote retains its original recovery receipt.
        if (!launchOptions.isRequestCurrent()) return true;
        setMessage('');
        navigateToCreatedWork(router, created);
        // Keep the spinner through a successful navigate so a second tap
        // cannot launch another task. If we never leave this screen, drop it.
        if (!hrefForCreatedWork(created)) { setWorkLaunching(false); resetRemoteLaunch(); }
        return true;
      } catch (err) {
        if (!launchOptions.isRequestCurrent()) return true;
        resetRemoteLaunch();
        setWorkLaunching(false);
        reportWorkLaunchError(err, { source: 'home_composer' });
        return false;
      }
    }

    // Debug: Log the formatted message to trace fast mode
    console.log('[app.jsx handleSend] formattedMessage:', {
      fastMode: formattedMessage.fastMode,
      hasText: !!formattedMessage.text,
      textPreview: formattedMessage.text?.substring(0, 50),
    });

    // Capture the 'new' context state BEFORE any async operations.
    // ChatInput's clearInput() runs synchronously after onSend() fires (during our await),
    // which deep-clears the 'new' context. We need to save these values now so we can
    // transfer them to the new conversation context later.
    const newContextState = useChatInputStore.getState().inputStates?.['new'];
    const savedSelectedParameters = newContextState?.selectedParameters || {};
    const savedSelectedButtons = newContextState?.selectedButtons || [];

    if (needsEmailVerification) {
      openModal('VerifyEmailModal', { source: 'home_composer_send' });
      return false;
    }

    const launchOptions = beginRemoteLaunch({
      mode: 'chat', prompt: formattedMessage.text || '', fileIds: formattedMessage.fileIds,
    });
    if (!launchOptions) return false;

    try {
      const connected = await socketManager.requireConnection();
      if (!launchOptions.isRequestCurrent()) return true;
      if (!connected) {
        resetRemoteLaunch();
        return false;
      }

      // The server checks the live balance; cached credits must not block a Chat send.

      const conversation = await createConversation({
        userId: userId,
        formattedMessage: formattedMessage,
        isTemporary: isTemporaryChat,
      });
      if (!launchOptions.isRequestCurrent()) return true;

      if (!conversation) {
        throw new Error(t('errors.conversationCreation'));
      }

      // Deep clear the 'new' context input BEFORE switching to the new conversation context
      // This must happen first, otherwise clearInput would clear the new conversation's context
      try { clearInput(true); } catch (_) {}

      // Pre-initialize the new conversation's context with the selected models/agents/parameters
      // so they persist when redirecting to the conversation page.
      // Skip this when _skipModelTransfer is set (e.g., quick-create auto-sends from Image/Video/Music/Web Search
      // buttons) - those are one-shot generations and should reset to Auto mode afterward.
      if (!formattedMessage._skipModelTransfer) {
        const hasModels = formattedMessage.modelIds?.length > 0;
        const hasAgents = formattedMessage.agentIds?.length > 0;
        const hasParameters = Object.keys(savedSelectedParameters).length > 0;
        const hasButtons = savedSelectedButtons.length > 0;
        if (hasModels || hasAgents || hasParameters || hasButtons) {
          const chatInputStore = useChatInputStore.getState();
          chatInputStore.setContext(conversation.id);
          if (hasModels) {
            chatInputStore.setSelectedModels(formattedMessage.modelIds);
          }
          if (hasAgents) {
            chatInputStore.setSelectedAgents(formattedMessage.agentIds);
          }
          // Transfer selectedParameters (action type, model-specific settings like duration/orientation)
          // and selectedButtons (tool selections) to the new conversation context.
          // This must happen AFTER setSelectedModels because setSelectedModels filters parameters
          // based on allowed parameter names - setting them after avoids the filtering removing
          // our transferred parameters.
          if (hasParameters || hasButtons) {
            useChatInputStore.setState(state => ({
              inputStates: {
                ...state.inputStates,
                [conversation.id]: {
                  ...state.inputStates[conversation.id],
                  ...(hasParameters ? { selectedParameters: savedSelectedParameters } : {}),
                  ...(hasButtons ? { selectedButtons: savedSelectedButtons } : {}),
                }
              }
            }));
          }
        }
      }

      // Show the thinking state immediately on navigation — kickConversation
      // still has preprocessing work before tokens arrive.
      useChatStore.getState().updateLocalChatDetails(conversation.id, {
        isInferencing: true,
        isInferencingWaiting: true,
      });

      router.push(`/c/${conversation.id}`);

      // Kick inference immediately — the conversation exists server-side and
      // the socket is already connected on the home composer.
      socketManager.emitKickConversation(userId, conversation.id);
      return true;
    } catch (error) {
      if (!launchOptions.isRequestCurrent()) return true;
      resetRemoteLaunch();
      console.error('Error creating conversation:', error.data);
      addToast(
        error.message || t('errors.conversationCreation'), 
        'error',
        error?.data?.actions
      );
      // Keep the composer text so the user can retry without retyping.
      return false;
    }
  }, [router, createConversation, addToast, t, userId, isPlus, needsEmailVerification, isTemporaryChat, setMessage, clearInput, openModal, beginRemoteLaunch, resetRemoteLaunch]);

  // Handle pending auto-send messages from Ideas, media pages, or web search.
  useEffect(() => {
    // Only proceed if we have pendingAutoSend AND userId is available.
    // Music can be instrumental (no lyrics/text) as long as a description was
    // provided, so allow create-music through even when text is empty.
    const hasUsablePayload = !!pendingAutoSend && (
      !!pendingAutoSend.text
      || (pendingAutoSend.action === 'create-music' && !!pendingAutoSend.description)
    );
    if (hasUsablePayload && userId && isAuthenticated) {
      // Chat can start immediately; handleSend waits for the socket connection.
      const timer = setTimeout(async () => {
        // Retained screens or a replaced request must not send this twice.
        if (useChatInputStore.getState().pendingAutoSend !== pendingAutoSend) return;
        // Store the pending data before clearing (in case we need it)
        const pendingData = { ...pendingAutoSend };
        
        // Clear the pending auto-send first to prevent duplicate sends
        clearPendingAutoSend();
        
        try {
          if (pendingData.action === 'chat') {
            // Use normal Chat defaults, without selecting a media model. Keep
            // the draft for retry if sending fails or an account gate blocks it.
            await handleSend({
              ...useChatInputStore.getState().getFormattedMessage('new'),
              text: pendingData.text,
            });
            return;
          }

          // Handle web search separately - no model needed
          if (pendingData.action === 'search-web') {
            const formattedMessage = {
              text: pendingData.text,
              parameters: {},
              actions: ['search-web'],
              fileIds: [],
              agentIds: [],
              modelIds: [],
              _skipModelTransfer: true, // Don't persist model/params to new conversation - reset to Auto
            };
            
            // Clear the input state since we're sending directly
            clearInput(true);
            // Send the message
            await handleSend(formattedMessage);
            return;
          }
          
          // Determine the fallback for this action. The intent-engine setting
          // is resolved below; these constants are only used when an admin
          // default has not been configured or is unavailable.
          let defaultSlug = DEFAULT_IMAGE_MODEL_SLUG;
          if (pendingData.action === 'create-video' || pendingData.action === 'image-to-video') {
            defaultSlug = DEFAULT_VIDEO_MODEL_SLUG;
          } else if (pendingData.action === 'create-music') {
            defaultSlug = DEFAULT_MUSIC_MODEL_SLUG;
          } else if (pendingData.action === 'create-3d') {
            defaultSlug = DEFAULT_3D_MODEL_SLUG;
          } else if (pendingData.action === 'image-to-3d') {
            defaultSlug = DEFAULT_IMAGE_TO_3D_MODEL_SLUG;
          }
          
          // Use an explicitly provided model, otherwise resolve the latest
          // admin-configured intent-engine default for every media action.
          let modelIdToUse = pendingData.modelId;
          
          if (!modelIdToUse) {
            const modelStore = useModelStore.getState();
            const { model: defaultModel, slug: resolvedSlug } = await resolveConfiguredOutputModel(
              modelStore,
              pendingData.action || 'create-image',
              defaultSlug,
              { force: true },
            );
            defaultSlug = resolvedSlug;
            
            modelIdToUse = defaultModel?.id;
          }
          
          // If we still don't have a model ID, try using the slug directly
          // (the server might accept slugs in modelIds)
          if (!modelIdToUse) {
            modelIdToUse = defaultSlug;
          }
          
          // Start from the chat input state so auto-send preserves model-specific
          // parameters selected before navigation, such as image aspect ratio.
          const stateFormattedMessage = useChatInputStore.getState().getFormattedMessage?.();
          const parameters = {
            ...(stateFormattedMessage?.parameters || {}),
            action: pendingData.action || stateFormattedMessage?.parameters?.action || 'create-image',
          };
          
          // Include model-specific parameters (e.g., description for music generation)
          // These need to be nested under modelParameters[modelId] to match getFormattedMessage structure
          if (modelIdToUse && pendingData.description) {
            parameters.modelParameters = {
              ...(parameters.modelParameters || {}),
              [modelIdToUse]: {
                ...(parameters.modelParameters?.[modelIdToUse] || {}),
                description: pendingData.description,
              }
            };
          }
          
          const formattedMessage = {
            text: pendingData.text,
            parameters,
            actions: [],
            fileIds: Array.isArray(pendingData.fileIds) ? pendingData.fileIds.filter(Boolean) : (stateFormattedMessage?.fileIds || []),
            agentIds: [],
            modelIds: [modelIdToUse], // Always include modelIds
            _skipModelTransfer: true, // Don't persist generation model to new conversation - reset to Auto
          };
          
          // Clear the input state since we're sending directly
          clearInput(true);
          // Send the message
          await handleSend(formattedMessage);
        } catch (error) {
          console.error('[app.jsx] Error auto-sending message:', error);
          addToast(error.message || t('home.failedToSend'), 'error');
        }
      }, pendingAutoSend.action === 'chat' ? 0 : 500);
      return () => clearTimeout(timer);
    }
  }, [pendingAutoSend, clearPendingAutoSend, clearInput, handleSend, userId, isAuthenticated, addToast]);

  const handleHomeModeChange = useCallback((mode) => {
    if (mode === 'work') {
      if (blockIfWorkModeDisabled()) return;
      const gated = requireAuth(() => true, {
        source: 'composer_coworker_mode',
        title: t('chatInput.coworkerMode.authTitle'),
        subtitle: t('chatInput.coworkerMode.authSubtitle'),
        icon: 'robot',
      });
      if (gated !== true) return;
      if (!isPlus) {
        openModal('PlusFeatureModal', { feature: 'work' });
        return;
      }
      setComposerMode('coworker');
      return;
    }
    const composer = useChatInputStore.getState();
    composer.setContext('new');
    setComposerMode('chat');
  }, [isPlus, openModal, requireAuth, setComposerMode, t]);

  // Home renders for both authenticated and logged-out visitors. Composer
  // submission gates anonymous visitors behind the auth CTA.

  const isCondensedForKeyboard = isKeyboardOpen && !isTemporaryChat;
  // Work chips + the keyboard tagline are taller than the leftover column on
  // a phone. Hide them once the IME is up so the field stays in the visible
  // area instead of sitting under the keyboard.
  const isShortKeyboardViewport = isKeyboardOpen
    && (
      windowHeight - (Platform.OS === 'android' ? 0 : effectiveKeyboardHeight)
    ) < 560;
  const homeActions = isAuthenticated && userId && !isGhost && !isTemporaryChat && !isWorkMode && !isKeyboardOpen && !workLaunching ? (
    <View style={styles.homeActions}>
      {!String(message || '').trim() && !hasComposerFiles && (
        <>
          {canUpgradeToPlus && (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('settingsMobile.upgradeToPlus')}
              onPress={() => openModal('PricingModal')}
              style={({ hovered, pressed }) => [
                styles.homeActionButton,
                (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
              ]}
            >
              <Text size="xs" color="secondary" style={styles.homeActionLabel}>
                {t('settingsMobile.upgradeToPlus')}
              </Text>
            </Pressable>
          )}
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('home.referralPrompt')}
            onPress={() => router.push('/referral')}
            style={({ hovered, pressed }) => [
              styles.homeActionButton,
              (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
            ]}
          >
            <Icon name="gift" size={12} color={colors.text_color_secondary} />
            <Text size="xs" color="secondary" style={styles.homeActionLabel}>
              {t('home.referralPrompt')}
            </Text>
          </Pressable>
        </>
      )}
    </View>
  ) : null;

  const showDiscovery = isAuthenticated && userId && !isGhost && !isTemporaryChat && !remoteLaunch;
  const homeHeight = homeViewportHeight || windowHeight - 100;
  const focusNewInput = useCallback(() => useChatInputStore.getState().requestInputFocus('new'), []);
  const discoveryNavigation = useHomeDiscoveryNavigation({
    enabled: !!showDiscovery && isFocused, native: isTouchNative, keyboardOpen: isKeyboardOpen,
    scrollRef: homeScrollRef, metricsRef: homeScrollMetrics, feedOffset: homeHeight,
    setMessage, focusInput: focusNewInput, contextKey: `${userId}:${isWorkMode}`,
  });

  const composer = (
    <View style={styles.inputContainer}>
      {!isNative && !isTemporaryChat && isKeyboardOpen && !isShortKeyboardViewport && (
        <>
          {selectedBot && !isShortMobileWeb && (
            <BotHero agent={selectedBot} compact />
          )}
          <Text
            style={styles.keyboardTagline}
            color='primary'
            weight='light'
          >
            {headline}
          </Text>
        </>
      )}
      {!isTemporaryChat && !isWorkMode && !isWorkModeDisabled && !isKeyboardOpen && (
        <WorkModeIntentSuggestions onActivate={() => handleHomeModeChange('work')} />
      )}
      {!isTemporaryChat && !isKeyboardOpen && !workLaunching && !String(message || '').trim() && (
        <View style={styles.starterSuggestions}>
          {isWorkMode ? (
            <WorkSuggestionChips compact hasFiles={hasComposerFiles} onSelect={setMessage} />
          ) : (
            <ChatStarterSuggestions hasFiles={hasComposerFiles} onSelect={setMessage} />
          )}
        </View>
      )}
      <ChatInputContainer forceInputStyle={CHAT_INPUT_STYLE.FLOATING}>
        <NewChatProjectBar isWorkMode={isWorkMode} isDisabled={workLaunching} />
        <ChatInput
          key={homeComposerKey}
          autoFocus={shouldAutofocusComposer}
          forceInputStyle={CHAT_INPUT_STYLE.FLOATING}
          placeholder={selectedBot ? (isWorkMode ? `What should ${selectedBot.name} work on?` : `Message ${selectedBot.name}…`) : isWorkMode ? WORK_COMPOSER_PLACEHOLDER : t(useShortChatPlaceholder ? 'home.inputPlaceholderMobile' : 'home.inputPlaceholder')}
          forcePlaceholder
          onSend={handleSend}
          value={message}
          onChangeText={setMessage}
          agentId={null}
          conversationId='new'
          isWorkMode={isWorkMode}
          hideVoiceMode={isWorkMode}
          isLoading={workLaunching}
          isDisabled={workLaunching}
          showBotPicker={presentAuthenticated && !isTemporaryChat}
          hideAgentTags={presentAuthenticated && !isTemporaryChat && !!selectedBot}
        />
      </ChatInputContainer>
      {!isMobile && homeActions}
    </View>
  );

  // Keep the composer outside the scrolling hero on phones and tablets.
  // A long paste must grow upward above the keyboard, never extend the page
  // below it and require a manual scroll to reach Send.
  const centerMobileWelcome = isMobile && showDiscovery && !isKeyboardOpen;
  const DiscoveryFeed = isWorkMode ? WorkDiscoveryFeed : HomeDiscoveryFeed;
  // The initial viewport is a complete welcome screen. Its invitation is fully
  // visible; actual discovery cards begin below the fold, regardless of height.
  const scrollHero = pinInputToBottom;
  const HeroContainer = scrollHero ? ScrollView : View;
  const pageContent = (
    <>
      <MetaHead 
        title={t('home.dashboard')}
        description={t('home.dashboardDescription')}
        noindex
      />
      <View style={styles.middleContainer}>
        {!isTemporaryChat && (
          <View style={styles.modeSwitch}>
            {isTouchNative && showDiscovery && isKeyboardOpen ? (
              <DiscoveryKeyboardActions onExplore={discoveryNavigation.explore} onDone={discoveryNavigation.done} />
            ) : (
              <ComposerModeSwitch
                value={isWorkMode ? 'work' : 'chat'}
                onChange={handleHomeModeChange}
                compact={isXS}
                pillsOnly
              />
            )}
          </View>
        )}
        <View style={[pinInputToBottom ? styles.pinnedColumn : styles.centeredContent, centerMobileWelcome && { justifyContent: 'center', paddingVertical: isShortMobileWeb ? 12 : 24 }]}>
          <HeroContainer
            style={[scrollHero ? styles.heroScroll : (pinInputToBottom ? styles.pinnedHero : undefined), centerMobileWelcome && { flex: 0, flexBasis: 'auto', flexGrow: 0, flexShrink: 0 }]}
            {...(scrollHero ? {
              contentContainerStyle: styles.centeredScrollContent,
              keyboardShouldPersistTaps: 'handled',
              keyboardDismissMode: Platform.isPad ? 'none' : (Platform.OS === 'ios' ? 'interactive' : 'on-drag'),
            } : {})}
          >
          <View style={[styles.titleContainer, centerMobileWelcome && { marginBottom: 24 }]}>
            {selectedBot && !isCondensedForKeyboard && !isShortMobileWeb && (
              <BotHero agent={selectedBot} compact={isNative || isXS} />
            )}
            {!isCondensedForKeyboard && <Text
              style={[styles.title, isShortMobileWeb && styles.titleCompact]}
              color='primary'
              weight='light'
              accessibilityRole="header"
              accessibilityLevel={1}
            >
              {headline}
            </Text>}
            {isMobile && !centerMobileWelcome && homeActions}
            {isTemporaryChat && (
              <Text 
                style={styles.subtitle}
                color='tertiary'
              >
                {t('home.temporaryChatSubtitle')}
              </Text>
            )}
          </View>

          </HeroContainer>
          {composer}
          {centerMobileWelcome && homeActions}
        </View>
      </View>
    </>
  );

  return (
    <>
    <BannerPlacement placement="home-banner" style={{ paddingHorizontal: 0, marginBottom: 0 }} />
    <View
      style={[styles.container, remoteLaunch && remoteLaunch.mode !== 'chat' && { paddingHorizontal: 0, paddingVertical: 0 }]}
      accessibilityLabel={headline}
    >
      {remoteLaunch ? <RemoteWorkLaunchPreview launch={remoteLaunch} /> : showDiscovery ? (
        <ScrollView ref={homeScrollRef} style={{ flex: 1, width: '100%' }} contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled" scrollEnabled={!isKeyboardOpen} scrollEventThrottle={100}
          onLayout={event => { if (!isTouchNative || !isKeyboardOpen) setHomeViewportHeight(event.nativeEvent.layout.height); }}
          onScroll={({ nativeEvent: { contentOffset, contentSize, layoutMeasurement } }) => {
            homeScrollMetrics.current = { y: contentOffset.y, height: layoutMeasurement.height };
            if (contentOffset.y > 30 && contentSize.height - contentOffset.y - layoutMeasurement.height < 1200) discoveryRef.current?.loadMore();
          }}
          onContentSizeChange={(_width, height) => {
            const metrics = homeScrollMetrics.current;
            if (metrics.y > 30 && height - metrics.y - metrics.height < 1200) discoveryRef.current?.loadMore();
          }}>
          <View style={{ height: homeHeight, width: '100%' }} testID="new-chat-welcome">
            <View style={{ flex: 1, minHeight: 0, width: '100%' }}>{pageContent}</View>
            {!isKeyboardOpen && <DiscoveryInvitation work={isWorkMode} onExplore={discoveryNavigation.explore} />}
          </View>
          {(isTouchNative || !isKeyboardOpen) && <View
            style={isKeyboardOpen ? { display: 'none' } : undefined}
            accessibilityElementsHidden={isKeyboardOpen} importantForAccessibility={isKeyboardOpen ? 'no-hide-descendants' : 'auto'}>
            <DiscoveryFeed ref={discoveryRef} enabled={showDiscovery} hasFiles={hasComposerFiles} onSelect={discoveryNavigation.selectIdea} />
          </View>}
        </ScrollView>
      ) : pageContent}
    </View>
    </>
  );
})

function HomeRoute() {
  const isFocused = useIsFocused();
  // Native-stack on web keeps pushed screens in the tree (display:none).
  // freezeOnBlur / unmountOnBlur do not drop those React trees, so a frozen
  // New Chat would keep `workLaunching` and show a stuck Send spinner after
  // Stop on a Work thread. Same stand-down as /c/:id (FocusedChatRoute).
  if (Platform.OS === 'web' && !isFocused) return null;
  return <HomePage />;
}

HomeRoute.displayName = 'HomeRoute';

export default React.memo(HomeRoute);
